/**
 * Scroll cache adapters for different cache managers
 * Provides generic interface to work with collection, pdp, and other cache managers
 */

import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import searchCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/SearchCacheStateManager'

import type { ScrollCacheManager } from '../contracts/scroll-cache'

/**
 * Adapter for CollectionCacheStateManager
 * Works with collection pages
 */
export const collectionScrollCacheAdapter: ScrollCacheManager = {
  getScrollPosition: (cacheKey: string): number | null => {
    const cachedData = collectionCacheStateManager.getCollectionCache(cacheKey)
    return cachedData?.scrollY ?? null
  },
  setScrollPosition: (cacheKey: string, scrollY: number): void => {
    // Collection cache manager requires full cache item, so we need to preserve existing data
    const currentCache = collectionCacheStateManager.getCollectionCache(cacheKey)
    if (currentCache) {
      collectionCacheStateManager.publishCollectionCache(cacheKey, scrollY)
    } else {
      // If no cache exists, create minimal one with scroll position
      // Note: This might not work perfectly if collection cache expects full structure
      // In practice, scroll is usually saved along with other state via publishCollectionCacheWithWidgetStates
      collectionCacheStateManager.publishCollectionCache(cacheKey, scrollY)
    }
  },
}

/**
 * Adapter for SearchCacheStateManager
 * Works with search pages
 */
export const searchScrollCacheAdapter: ScrollCacheManager = {
  getScrollPosition: (cacheKey: string): number | null => {
    const cachedData = searchCacheStateManager.getSearchCache(cacheKey)
    return cachedData?.scrollY ?? null
  },
  setScrollPosition: (cacheKey: string, scrollY: number): void => {
    searchCacheStateManager.publishSearchCache(cacheKey, scrollY)
  },
}

/**
 * Default adapter (uses collection cache)
 * Maintains backward compatibility
 */
export const defaultScrollCacheAdapter = collectionScrollCacheAdapter
